/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from "@emotion/core";
import React, { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { Store } from "store";
import { Timestamp } from "@google-cloud/firestore";

import { CommentFilterType } from "modules/streaming";

import { EventInfo } from "utility";

import { StreamingActionHeader } from "./StreamingActionHeader";
import { Box } from "@material-ui/core";
import { StreamingAnnouncement } from "./StreamingAnnouncement";
import StreamingCommentV2 from "./StreamingCommentV2";
import { useEmoBalance } from "hooks/paw/useEmoBalance";
import StreamingLiveCommerceV2 from "./StreamingLiveCommerceV2";
import { StreamingSelectGiftV2 } from "./StreamingSelectGiftV2";
import { StreamingSendCommentV2 } from "./StreamingSendCommentV2";
import { StreamingCommentHidden } from "./StreamingCommentHidden";
import { ActionAreaType } from "./useActionAreaType";
import { useSendComment } from "./useSendComment";
import { isPopOut } from "./enableCommentPopOut";
import type { PlainVideoPlayer } from "./videojsPlayer";
import { StreamingLiveEventV2 } from "./StreamingLiveEventV2";

import { StreamingLiveCommercePurchasedToast } from "./liveCommerce/StreamingLiveCommercePurchasedToast";
import { useLiveCommerce } from "./useLiveCommerce";
import { PurchasingModal } from "ui/Streaming/LiveCommerce/PurchasingModal";

type Props = {
  event: EventInfo;
  player: PlainVideoPlayer | null;
  eventStart: Timestamp;
  mainVideoId: string;
  THEOPlayerElementHeight: number;
  isChatAdmin: boolean;
  isHiddenStreamingComment: boolean;
  isPollingEnabled: boolean;
  isLiveCommerceEnabled: boolean;
  isOpenAnnouncement: boolean;
  handleToggleAnnouncement: () => void;
  isEditingAnnouncement: boolean;
  setIsEditingAnnouncement: (isEditing: boolean) => void;
  streamingAnnouncement: string;
  setStreamingAnnouncement: (announcement: string) => void;
  onStreamingAnnouncementLocalDeleted: () => void;
  isOpenLiveEvent: boolean;
  handleToggleLiveEvent: () => void;
  actionAreaType: ActionAreaType;
  selectedCommentFilter: CommentFilterType;
  handleChangeCommentFilter: (filter: CommentFilterType) => void;
  handleCloseActionArea: () => void;
  hasDvrStartTime: boolean;
  isLiveEventEnabled: boolean;
};

/**
 * UserActionArea
 * comment, gift, (later: goods sales, quiz, vote)
 * @param props
 */
const StreamingActionAreaV2: React.FC<Props> = (props) => {
  // ----- Hooks -----
  const { paidValue: emoBalance } = useEmoBalance();
  const {
    commentInputRef,
    iconUrls,
    comment,
    giftItems,
    selectedGiftItem,
    canSendComment,
    canPurchaseGiftItem,
    isOpenGiftDrawer,
    handleSetComment,
    handleSendComment,
    handleOpenGiftDrawer,
    handleCloseGiftDrawer,
    handleClickSelectedGift,
    handleOpenPawChargeModal,
  } = useSendComment({
    mainVideoId: props.mainVideoId,
  });
  const { isPurchased } = useLiveCommerce();

  // ----- Redux State -----
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );
  const user = useSelector((state: Store) => state.auth.user);
  const streamingType = useSelector(
    (state: Store) => state.streaming.streamingType
  );

  const streamingAnnouncement = (
    <StreamingAnnouncement
      eventId={props.event.eventId}
      mainVideoId={props.mainVideoId}
      streamingType={streamingType}
      isOpenAnnouncement={props.isOpenAnnouncement}
      isEditingAnnouncement={props.isEditingAnnouncement}
      setIsEditingAnnouncement={props.setIsEditingAnnouncement}
      streamingAnnouncement={props.streamingAnnouncement}
      setStreamingAnnouncement={props.setStreamingAnnouncement}
      onStreamingAnnouncementLocalDeleted={
        props.onStreamingAnnouncementLocalDeleted
      }
    />
  );

  const [showPurchasingModal, setShowPurchasingModal] =
    React.useState<boolean>(false);
  const [showPurchasedToast, setShowPurchasedToast] = React.useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = React.useState<
    "Card" | "CVS" | "Phone" | null
  >(null);

  const { handleCloseActionArea } = props;

  useEffect(() => {
    if (isPurchased && selectedPaymentType !== "Phone") {
      handleCloseActionArea();
      setShowPurchasedToast(true);
    }
  }, [isPurchased, selectedPaymentType, handleCloseActionArea]);
  // isPurchasedに依存するとクリーンアップが実行されてしまうためトーストの表示とタイマーは別のuseEffectにしている
  useEffect(() => {
    if (showPurchasedToast) {
      const timer = setTimeout(() => {
        setShowPurchasedToast(false);
      }, 6000);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [showPurchasedToast]);

  const streamingCommentV2 = (
    <StreamingCommentV2
      player={props.player}
      eventStart={props.eventStart}
      streamingType={streamingType}
      eventInfo={props.event}
      mainVideoId={props.mainVideoId}
      selectedCommentFilter={props.selectedCommentFilter}
      THEOPlayerElementHeight={props.THEOPlayerElementHeight}
      hasDvrStartTime={props.hasDvrStartTime}
      isPollingEnabled={props.isPollingEnabled}
    />
  );

  const streamingSendCommentV2 = streamingType === "Live" && user.uid && (
    <StreamingSendCommentV2
      isDarkModeEnabled={isDarkModeEnabled}
      isOpenGiftDrawer={isOpenGiftDrawer}
      isSelectedGift={selectedGiftItem !== null}
      canPurchaseGiftItem={canPurchaseGiftItem}
      userIcon={iconUrls?.["32"]}
      userName={user.displayName ?? ""}
      inputRef={commentInputRef}
      comment={comment}
      canSendComment={canSendComment}
      giftItems={giftItems}
      onClickSendComment={handleSendComment}
      setComment={handleSetComment}
      onClickOpenGiftDrawer={handleOpenGiftDrawer}
      onClickOpenPawChargeModal={handleOpenPawChargeModal}
    />
  );

  const streamingActionHeader = (
    <StreamingActionHeader
      isDarkModeEnabled={isDarkModeEnabled}
      handleChangeCommentFilter={props.handleChangeCommentFilter}
      handleToggleAnnouncement={props.handleToggleAnnouncement}
      handleCloseActionArea={
        isPopOut() ? props.handleCloseActionArea : undefined
      }
      selectedCommentFilter={props.selectedCommentFilter}
      showCommentFilter={props.actionAreaType === "comment"}
      showAnnouncement={props.isChatAdmin || props.streamingAnnouncement !== ""}
      isLiveEventEnabled={props.isLiveEventEnabled}
      handleToggleLiveEvent={props.handleToggleLiveEvent}
    />
  );

  const streamingSelectGiftV2 = isOpenGiftDrawer && giftItems.length > 0 && (
    <StreamingSelectGiftV2
      isDarkModeEnabled={isDarkModeEnabled}
      canPurchaseGiftItem={canPurchaseGiftItem}
      iconUrls={iconUrls?.["32"]}
      userName={user.displayName ?? ""}
      comment={comment}
      emoBalance={emoBalance}
      giftItems={giftItems}
      selectedGiftItem={selectedGiftItem}
      canSendComment={canSendComment}
      onClickSendComment={handleSendComment}
      onClickCloseGiftDrawer={handleCloseGiftDrawer}
      onClickSelectedGift={handleClickSelectedGift}
      onClickOpenPawChargeModal={handleOpenPawChargeModal}
    />
  );

  if (isPopOut()) {
    return (
      <StreamingActionAreaLayout
        className={"streaming_action_area_height"}
        header={streamingActionHeader}
      >
        {streamingAnnouncement}
        {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

        <Box flex={"1 1 auto"}>{streamingCommentV2}</Box>
        {streamingSelectGiftV2}
        {streamingSendCommentV2}
      </StreamingActionAreaLayout>
    );
  }

  return (
    <React.Fragment>
      {props.actionAreaType === "comment" &&
        !props.isHiddenStreamingComment && (
          <StreamingActionAreaLayout
            className={"streaming_action_area_height"}
            header={streamingActionHeader}
          >
            {streamingAnnouncement}
            {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

            <Box flex={"1 1 auto"}>{streamingCommentV2}</Box>
            {streamingSelectGiftV2}
            {streamingSendCommentV2}
            <StreamingLiveCommercePurchasedToast
              showPurchasedToast={showPurchasedToast}
            />
          </StreamingActionAreaLayout>
        )}

      {props.actionAreaType === "goods" && props.isLiveCommerceEnabled && (
        <StreamingActionAreaLayout
          className={"streaming_action_area_height"}
          header={streamingActionHeader}
        >
          {streamingAnnouncement}
          {props.isOpenLiveEvent && <StreamingLiveEventV2 />}

          <Box position={"relative"} flex={"1 1 auto"} paddingBottom={"60px"}>
            <StreamingLiveCommerceV2
              mainVideoId={props.mainVideoId}
              THEOPlayerElementHeight={props.THEOPlayerElementHeight}
              setSelectedPaymentType={setSelectedPaymentType}
              setShowPurchasingModal={setShowPurchasingModal}
            />
          </Box>

          {showPurchasingModal && <PurchasingModal />}
        </StreamingActionAreaLayout>
      )}

      {props.isHiddenStreamingComment && (
        <StreamingCommentHidden
          THEOPlayerElementHeight={props.THEOPlayerElementHeight}
        />
      )}
    </React.Fragment>
  );
};
export default StreamingActionAreaV2;

const StreamingActionAreaLayout: FC<{
  children: React.ReactNode;
  header: React.ReactNode;
  className: string;
}> = ({ children, header, className }) => {
  const popOutMode = isPopOut();
  const isDarkModeEnabled = useSelector(
    (state: Store) => state.streaming.streamingSettings.isDarkModeEnabled
  );

  return (
    <Box
      className={`streaming_action_area ${className}`}
      position={"relative"}
      display={"flex"}
      flexDirection={"column"}
      gridGap={"12px"}
      padding={"12px"}
      {...(popOutMode && {
        height: "100vh",
        marginX: "auto",
        bgcolor: isDarkModeEnabled ? "#202020" : "#fff",
      })}
    >
      {header}
      {children}
    </Box>
  );
};
